import { useEffect, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export const OrderWisefavouritedReport = memo(() => {
  const { t } = useTranslation();
  const currentTime = new Date();
  const currentyear = currentTime.getFullYear();
  const currentMonth = currentTime.getUTCMonth() + 1;

  const getCurrentFiscalYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const startYear = month >= 9 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
  };

  const [reportType, setReportType] = useState("monthly");
  const [period, setPeriod] = useState(getCurrentFiscalYear());
  const [isLoading, setIsLoading] = useState(true);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  const noDataPlugin = {
    id: "noDataPlugin",
    beforeDraw: (chart) => {
      const { datasets } = chart.data;
      const hasData = datasets.some(
        (dataset) => dataset.data && dataset.data.length > 0,
      );
      const ctx = chart.ctx;
      ctx.save();
      ctx.clearRect(0, 0, chart.width, chart.height);
      if (!hasData) {
        const { width, height } = chart;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "16px Arial";
        ctx.fillStyle = "#eb9f9d";
        ctx.fillText(t("no_data_filtered_search_txt"), width / 2, height / 2);
      }
      ctx.restore();
    },
  };

  const quarterNames = {
    1: t("quarter_sep_nov"),
    2: t("quarter_dec_feb"),
    3: t("quarter_mar_may"),
    4: t("quarter_jun_aug"),
  };

  const getLabels = (data, type) => {
    if (type === "weekly") {
      return data.map((item) => {
        const date = new Date(item._id.week);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      });
    }
    if (type === "monthly") {
      return data.map((item) => `${item._id.month}/${item._id.year}`);
    }
    if (type === "quarterly") {
      return data.map((item) => quarterNames[item._id.quarter]);
    }
    return data.map((item) => item._id.year);
  };

  useEffect(() => {
    setIsLoading(true);
    http
      .post(BaseUrl + "admin/orderwisefavouritereport", {
        reportType,
        period,
      })
      .then((response) => {
        const data = response?.data?.data || [];
        const labels = getLabels(data, reportType);
        const dataset = {
          label: t("favourite_orders_txt"),
          data: data.map((item) => item.orderCount),
          revenueData: data.map((item) => item.amount),
          borderColor: "rgb(54, 162, 235)",
          backgroundColor: "rgba(54, 162, 235, 0.3)",
          tension: 0.3,
        };
        setChartData({
          labels,
          datasets: [dataset],
        });
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  }, [reportType, period]);

  const changeHandler = (e) => {
    setReportType(e.target.value);
  };

  const changePeriodHandler = (e) => {
    setPeriod(e.target.value);
  };

  const options = [];

  for (let i = currentyear; i >= 2023; i--) {
    if (i === currentyear && currentMonth < 9) {
      continue;
    }
    options.push(
      <option key={`${i}-${i + 1}`} value={`${i}-${i + 1}`}>
        {`${i}-${i + 1}`}
      </option>,
    );
  }

  function centerSinglePointDatasets(chartData) {
    const labels = chartData.labels;
    const updatedDatasets = chartData.datasets.map((dataset) => {
      const validPoints = dataset.data
        .map((val, i) => ({ val, i }))
        .filter(({ val }) => val?.y > 0);
      if (validPoints.length === 1) {
        const onlyIndex = validPoints[0].i;
        const centeredData = labels.map((_, i) =>
          i === onlyIndex ? validPoints[0].val : null,
        );
        return {
          ...dataset,
          data: centeredData,
          spanGaps: false,
          pointRadius: 4,
          pointHoverRadius: 6,
        };
      }
      return dataset;
    });
    return {
      ...chartData,
      datasets: updatedDatasets,
    };
  }

  const loadingbuffer = isLoading ? "loading-buffer" : "";

  return (
    <div
      className={`chart-container chart-order-wise-favourite-report ${loadingbuffer}`}
      style={{ verticalAlign: "top" }}
    >
      <div className="chart-Hdr">
        <div className="chart-Hdr-txt">
          <h2>{t("order_wise_favourite_report_txt")}</h2>
        </div>
        <div className="chart-fltr">
          <select
            style={{ width: "150px" }}
            value={reportType}
            onChange={changeHandler}
          >
            <option value="weekly">{t("current_week_txt")}</option>
            <option value="monthly">{t("monthly_txt")}</option>
            <option value="quarterly">{t("quarterly_txt")}</option>
            <option value="yearly">{t("fy_data_txt")}</option>
          </select>
          {(reportType === "monthly" ||
            reportType === "quarterly" ||
            reportType === "yearly") && (
            <select
              style={{ width: "170px" }}
              value={period}
              onChange={changePeriodHandler}
            >
              {options}
            </select>
          )}
        </div>
      </div>
      {chartData && (
        <Line
          data={centerSinglePointDatasets(chartData)}
          plugins={[noDataPlugin]}
          options={{
            responsive: true,
            plugins: {
              legend: {
                display: false,
              },
              title: {
                display: false,
              },
              datalabels: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (tooltipItem) => {
                    const revenue =
                      tooltipItem.dataset.revenueData?.[
                        tooltipItem.dataIndex
                      ] || 0;

                    return [
                      `${t("no_of_orders_txt")} (#): ${tooltipItem.raw}`,
                      `${t("total_revenue_txt")}: ${Number(revenue).toLocaleString(
                        "en-GB",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}`,
                    ];
                  },
                },
              },
            },
            scales: {
              x: {
                offset: true,
                grid: {
                  display: false,
                },
                ticks: {
                  align: "center",
                },
              },
              y: {
                beginAtZero: true,
                title: {
                  display: false,
                },
              },
            },
          }}
        />
      )}
    </div>
  );
});
